// src/pages/settings/xoe/XoePermissionsTab.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Checkbox, Button, Space, Alert, Popconfirm, Typography } from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";

// Shared components
import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";

// API
import {
	fetchXoeSettings,
	updateXoeSettings,
	resetXoeSettings,
} from "../../../api/xoe/XoeSettingsApi";

// Types
import { XoeClinicSettings } from "../../../types/xoe/XoeSettingsTypes";

// Utils
import { extractValidationErrors } from "../../../utils/apiError";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";

const { Text } = Typography;

/**
 * Turn a wire tool name into a practitioner-facing label, MECHANICALLY.
 *
 * `search_patient_history` → "Patient History"; `list_available_tests` → "Available Tests".
 *
 * xoe-agent has a richer label map (`app/tool_labels.py`, e.g. "Reading the comprehensive
 * analysis") but it is server-side, shaped for SSE progress frames, and NOT served by the
 * settings endpoint. Copying it into this repo is the cross-repository drift that
 * `XoeToolOption` exists to prevent, so the weaker label is accepted deliberately and the
 * API-served `description` renders underneath each checkbox to carry the meaning.
 *
 * Known divergence, accepted: the admin frontend labels `search_labs` "Lab Results" where
 * this yields "Labs". Special-casing it would be the first entry of exactly the label table
 * this approach avoids. The upside is that a tool added to `app/tool_registry.py` appears
 * here, correctly labelled, with no frontend change at all.
 */
export const toolLabel = (name: string): string =>
	name
		.replace(/^(search|list)_/, "")
		.split("_")
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ") || name;

/**
 * The clinic's Xoe tool-permission settings (P5.2).
 *
 * S4 (ClickUp 86d42twq8) — converted from the standalone `/settings/xoe` route
 * (`XoeSettingsPage.tsx`) into the settings drawer's CLINICAL group. This screen never
 * rendered `Layout` (a pre-existing inconsistency S4 happens to resolve); it did render
 * `PageContainer` in all three return branches, and that is stripped from all three here.
 * The loading and load-failed branches keep their `InlineLoading` / error `Alert` +
 * Retry — this screen's failure mode (xoe-agent unreachable) is one an operator is
 * expected to see and report, and losing the message to a stripped wrapper would turn it
 * into a blank tab.
 *
 * Follows `AppointmentSettingsTab` — plain `useState`, direct API calls (not React Query),
 * save/reset in the card's `extra`, `extractValidationErrors` for 422 surfacing — because
 * that is the established shape for a clinic-singleton setting.
 *
 * *** IT DELIBERATELY USES NEITHER AntD `Form` NOR `Checkbox.Group`, unlike that tab. ***
 * There is one field here and zero validation rules, so `Form` buys nothing. Both were
 * tried and both produced the SAME silent data-loss bug, from the same root cause:
 * `Checkbox.Group` does not report which boxes are ticked. It emits
 * `newValue.filter(v => registeredValues.includes(v))`, and that registry — populated by
 * child mount effects — comes back empty on this page. Ticking one tool therefore emitted
 * `[]`, so the next Save would have denied Xoe every source the clinic still had enabled.
 * On a permissions screen that is the worst possible failure: it looks like it worked.
 * Owning the array in `useState` and controlling each `Checkbox` removes the dependency on
 * that machinery. Pinned by "inverts the ticks back into disabled_tools".
 *
 * ── THE POLARITY INVERSION, the only real logic on this screen ──
 * Storage is a DENY-list (`disabled_tools` = switched OFF). A practitioner reads a settings
 * screen as "what Xoe can do". So CHECKED means ALLOWED:
 *
 *     load:  checked = available_tools − disabled_tools
 *     save:  disabled_tools = available_tools − checked
 *
 * A useful property falls out of deriving the save side from `available_tools` rather than
 * from the loaded deny-list: a stored name no longer in the registry (the rename hazard
 * `clinic_settings_routes.py` warns about — a stale entry 422s the next otherwise
 * unmodified PUT) is simply dropped, so the screen self-heals instead of jamming. Because
 * that is a silent data change it is DISCLOSED inline rather than done quietly.
 */
const XoePermissionsTab: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const [saving, setSaving] = useState<boolean>(false);
	const [settings, setSettings] = useState<XoeClinicSettings | null>(null);
	const [allowedTools, setAllowedTools] = useState<string[]>([]);
	const [loadFailed, setLoadFailed] = useState<boolean>(false);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadSettings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/** Apply a server payload to local state — this is the polarity inversion, load side. */
	const applySettings = (data: XoeClinicSettings) => {
		setSettings(data);
		const disabled = new Set(data.disabled_tools);
		setAllowedTools(
			data.available_tools.map((tool) => tool.name).filter((name) => !disabled.has(name))
		);
	};

	const loadSettings = async () => {
		setLoading(true);
		try {
			applySettings(await fetchXoeSettings());
			setLoadFailed(false);
		} catch (error) {
			// This screen is reachable wherever VITE_XOE_AGENT_SERVER is set, but xoe-agent may
			// not actually be answering there yet. Render an explicit failed state rather than an
			// empty form, which would read as "this clinic has no tools".
			setLoadFailed(true);
			showError("Failed to load Xoe settings", extractValidationErrors(error).join(" "));
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Stored deny entries that are no longer known tools. They cannot be rendered (there is
	 * no checkbox for a tool that does not exist) and the inversion will drop them on the
	 * next save, so the practitioner is told before they press Save.
	 */
	const staleRestrictions = useMemo(() => {
		if (!settings) return [];
		const known = new Set(settings.available_tools.map((tool) => tool.name));
		return settings.disabled_tools.filter((name) => !known.has(name));
	}, [settings]);

	const handleSave = async () => {
		if (!settings) return;

		const allowed = new Set(allowedTools);
		const disabledTools = settings.available_tools
			.map((tool) => tool.name)
			.filter((name) => !allowed.has(name));

		try {
			setSaving(true);
			// `disabled_actions` is NOT sent — see XoeSettingsApi. Sending it would clear an axis
			// this screen cannot render.
			applySettings(await updateXoeSettings({ disabled_tools: disabledTools }));
			showSuccess("Xoe settings saved successfully");
		} catch (error) {
			// Surfaces xoe-agent's FastAPI `{detail}` 422 (e.g. an unknown tool name), which
			// extractValidationErrors learned to read for this screen.
			showError("Failed to save Xoe settings", extractValidationErrors(error).join(" "));
		} finally {
			setSaving(false);
		}
	};

	const handleReset = async () => {
		try {
			setLoading(true);
			applySettings(await resetXoeSettings());
			showSuccess("Xoe settings reset to defaults");
		} catch (error) {
			showError("Failed to reset Xoe settings", extractValidationErrors(error).join(" "));
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Toggle one tool.
	 *
	 * *** DO NOT REPLACE THIS WITH AntD `Checkbox.Group`'s `onChange`. ***
	 * That component does not hand back "the boxes that are ticked". It computes
	 * `newValue.filter(v => registeredValues.includes(v))`, where `registeredValues` is
	 * built by each child `Checkbox` registering itself in a mount effect
	 * (`antd/lib/checkbox/Group.js`, `toggleOption`). On this page that list comes back
	 * EMPTY, so ticking one box emitted `[]` — every other tool was silently dropped and
	 * the next Save would have denied Xoe every source the clinic had left enabled. Owning
	 * the array here removes the dependency on that registration entirely; it is also less
	 * code. Pinned by "inverts the ticks back into disabled_tools".
	 */
	const toggleTool = (name: string, checked: boolean) =>
		setAllowedTools((current) =>
			checked ? [...current, name] : current.filter((tool) => tool !== name)
		);

	const selectAll = () =>
		setAllowedTools(settings ? settings.available_tools.map((tool) => tool.name) : []);

	const selectNone = () => setAllowedTools([]);

	if (loading && !settings) {
		return <InlineLoading minHeight={300} message="Loading Xoe settings…" />;
	}

	if (loadFailed && !settings) {
		return (
			<SectionCard title="What Xoe can read">
				<Alert
					type="error"
					showIcon
					message="Xoe settings are unavailable"
					description="The Xoe assistant service could not be reached, so its permissions cannot be shown or changed right now. Xoe's existing permissions are unaffected."
					action={<Button onClick={loadSettings}>Retry</Button>}
				/>
			</SectionCard>
		);
	}

	const saveActions = (
		<Space>
			{/* Reset clears BOTH deny-lists server-side, including action restrictions this screen
			    cannot show — so it is confirmed, and the copy says exactly that rather than letting
			    a practitioner clear something they never saw. */}
			<Popconfirm
				title="Reset Xoe permissions?"
				description="This switches every tool back on and also clears any action restrictions set by an administrator, which are not shown on this screen."
				okText="Reset everything"
				cancelText="Cancel"
				onConfirm={handleReset}
			>
				<Button icon={<ReloadOutlined />} loading={loading}>
					Reset to Defaults
				</Button>
			</Popconfirm>
			<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
				Save Changes
			</Button>
		</Space>
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="xoe-settings-page">
			<SectionCard
				title="What Xoe can read"
				subtitle="Xoe may only look at the information you tick here. Unticking one does not delete anything — Xoe simply stops consulting it."
				extra={saveActions}
			>
				{staleRestrictions.length > 0 && (
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 16 }}
						message={
							staleRestrictions.length === 1
								? "1 restriction no longer applies"
								: `${staleRestrictions.length} restrictions no longer apply`
						}
						description="They refer to information sources Xoe no longer has. They will be cleared the next time you save, which does not change what Xoe can currently read."
					/>
				)}

				<Space style={{ marginBottom: 12 }}>
					<Button size="small" onClick={selectAll}>
						Select all
					</Button>
					<Button size="small" onClick={selectNone}>
						Select none
					</Button>
				</Space>

				{/*
				  Individually-controlled Checkboxes, NOT a `Checkbox.Group`.
				  See the toggleTool docblock — Checkbox.Group's onChange is computed against
				  an internally-registered value list that is empty here, so it silently drops
				  every tool the user did not just click.
				*/}
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{settings?.available_tools.map((tool) => (
						<Checkbox
							key={tool.name}
							value={tool.name}
							checked={allowedTools.includes(tool.name)}
							onChange={(event) => toggleTool(tool.name, event.target.checked)}
						>
							<span style={{ fontWeight: 500 }}>{toolLabel(tool.name)}</span>
							<Text
								type="secondary"
								style={{ display: "block", fontSize: 12, fontWeight: 400 }}
							>
								{tool.description}
							</Text>
						</Checkbox>
					))}
				</div>

				<Text type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
					{settings?.configured && settings.updated_at
						? `Last changed ${new Date(settings.updated_at).toLocaleString()}.`
						: "No changes have been made yet — Xoe can read everything listed above."}
				</Text>
			</SectionCard>

			{/*
			  Write actions get the stronger affordance the ticket asks for. It is a real section
			  rather than toggles, because there is nothing honest to toggle YET: the settings
			  endpoint serves no `available_actions` vocabulary (it exists only in healthcare's
			  fetched catalogue), and inventing the action strings here is the drift that fetch
			  exists to prevent. Shipping non-functional toggles to practitioners is worse than
			  not shipping them.
			*/}
			<SectionCard title="What Xoe can propose" subtitle="Changes to the patient record">
				<Alert
					type="info"
					showIcon
					message="Xoe never changes a record on its own"
					description="Anything Xoe suggests — a note, a medication, a test order — is a proposal. It is only written to the patient's record after you review it and confirm, and it is recorded against your name, not Xoe's."
				/>
				<Alert
					type="warning"
					showIcon
					style={{ marginTop: 12 }}
					message="Which changes Xoe may propose is managed by your administrator"
					description="These are not editable here yet. Reset to Defaults above will clear any restrictions an administrator has set."
				/>
			</SectionCard>
		</div>
	);
};

export default XoePermissionsTab;
